import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  CreateInsuranceRegulatoryDefinitionPayload,
  CreateInsuranceRegulatoryReportJobPayload,
  InsuranceApiService,
  InsuranceRegulatoryReportDefinitionListItem,
  InsuranceRegulatoryReportJobListItem
} from './insurance-api.service';
import {
  INSURANCE_LIST_TOOLBAR_STYLES,
  SortOrder,
  compareSortValues,
  downloadCsv,
  liveStatusKey,
  subscribeInsuranceOperationsLiveUpdates,
  toggleSortField
} from './insurance-list-page.utils';

type RegulatoryJobSortField = 'definitionCode' | 'definitionName' | 'jurisdiction' | 'periodFrom' | 'status';

@Component({
  standalone: true,
  selector: 'ain-insurance-regulatory-jobs-shell',
  imports: [
    AinDatePipe,
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page
      [title]="t('insurance.regulatory.console.title')"
      [description]="t('insurance.regulatory.console.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.regulatory.help.page.title'"
        [descriptionKey]="'insurance.regulatory.help.page.description'"
        [bulletKeys]="[
          'insurance.regulatory.help.page.bullets.scope',
          'insurance.regulatory.help.page.bullets.definitions',
          'insurance.regulatory.help.page.bullets.selection'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (permissionDenied()) {
        <ain-state type="error" [title]="t('insurance.states.noPermission')" [message]="t('insurance.states.noPermissionMessage')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.regulatory.console.actions.createDefinition')" (click)="createDefinition()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.regulatory.console.actions.createJob')" (click)="createJob()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.bulk')" (click)="applyBulk()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.export')" (click)="exportRows()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
            <span class="live-status" [class]="liveUpdateService.connectionState()">
              {{ t(liveStatusKey(liveUpdateService.connectionState())) }}
            </span>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.regulatory.help.listFilters.title'"
                [descriptionKey]="'insurance.regulatory.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.regulatory.help.listFilters.bullets.search',
                  'insurance.regulatory.help.listFilters.bullets.status',
                  'insurance.regulatory.help.listFilters.bullets.jurisdiction'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.regulatory.console.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.regulatory.console.filters.status') }}</span>
                <p-select
                  [options]="statusOptions"
                  optionValue="value"
                  [ngModel]="statusFilter()"
                  (ngModelChange)="onStatusFilterChange($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('insurance.regulatory.console.filters.jurisdiction') }}</span>
                <p-select
                  [options]="jurisdictionOptions"
                  optionValue="value"
                  [ngModel]="jurisdictionFilter()"
                  (ngModelChange)="onJurisdictionFilterChange($event)"
                  [showClear]="true"
                  appendTo="body">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? item.label : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ item.label }}</ng-template>
                </p-select>
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.regulatory.console.states.empty')" [message]="t('insurance.regulatory.console.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('definitionCode')">{{ t('insurance.regulatory.console.fields.code') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('definitionName')">{{ t('insurance.regulatory.console.fields.name') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('jurisdiction')">{{ t('insurance.regulatory.console.fields.jurisdiction') }}</button></th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('periodFrom')">{{ t('insurance.regulatory.console.fields.periodFrom') }}</button></th>
                  <th>{{ t('insurance.regulatory.console.fields.periodTo') }}</th>
                  <th><button type="button" class="sort-btn" (click)="sortBy('status')">{{ t('columns.status') }}</button></th>
                  <th>{{ t('insurance.regulatory.console.fields.submittedAt') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.definitionCode }}</button></td>
                  <td>{{ row.definitionName }}</td>
                  <td>{{ row.jurisdiction }}</td>
                  <td>{{ row.periodFrom | ainDate }}</td>
                  <td>{{ row.periodTo | ainDate }}</td>
                  <td>{{ t(statusKey(row.status)) }}</td>
                  <td>{{ row.submittedAtUtc ? (row.submittedAtUtc | ainDate) : '-' }}</td>
                </tr>
              </ng-template>
            </p-table>
          }

          @if (info()) { <p class="info">{{ info() }}</p> }
          @if (error()) { <p class="error">{{ error() }}</p> }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; }
    .panel { display: grid; gap: 10px; }
    .filters { border: 1px solid #e5e7eb; border-radius: 10px; padding: 8px 10px; background: #f9fafb; }
    .filters summary { display: flex; align-items: center; gap: 8px; }
    .filter-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; margin-top: 8px; }
    .filter-grid label { display: grid; gap: 6px; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; }
    .error { color: #b91c1c; }
    @media (max-width: 760px) { .filter-grid { grid-template-columns: 1fr; } }
    ${INSURANCE_LIST_TOOLBAR_STYLES}
  `]
})
export class InsuranceRegulatoryJobsShellComponent implements OnInit, OnDestroy {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly currentContext = inject(CurrentContextService);
  readonly liveUpdateService = inject(LiveUpdateService);
  private readonly localization = inject(LocalizationService);
  private liveUnsubscribe: (() => Promise<void>) | null = null;

  readonly loading = signal(true);
  readonly permissionDenied = signal(false);
  readonly error = signal('');
  readonly info = signal('');
  readonly jobs = signal<InsuranceRegulatoryReportJobListItem[]>([]);
  readonly definitions = signal<InsuranceRegulatoryReportDefinitionListItem[]>([]);
  readonly selectedRows = signal<InsuranceRegulatoryReportJobListItem[]>([]);
  readonly search = signal('');
  readonly statusFilter = signal<string | null>(null);
  readonly jurisdictionFilter = signal<string | null>(null);
  readonly sortField = signal<RegulatoryJobSortField>('definitionCode');
  readonly sortOrder = signal<SortOrder>(1);

  liveStatusKey = liveStatusKey;

  readonly statusOptions = [
    { value: 'Pending', labelKey: 'insurance.regulatory.console.status.pending' },
    { value: 'Validating', labelKey: 'insurance.regulatory.console.status.validating' },
    { value: 'Ready', labelKey: 'insurance.regulatory.console.status.ready' },
    { value: 'Submitted', labelKey: 'insurance.regulatory.console.status.submitted' },
    { value: 'Failed', labelKey: 'insurance.regulatory.console.status.failed' }
  ];
  readonly jurisdictionOptions = [
    { value: 'IR', label: 'IR' },
    { value: 'EU', label: 'EU' },
    { value: 'Custom', label: 'Custom' }
  ];

  ngOnInit(): void {
    this.search.set(this.route.snapshot.queryParamMap.get('q') ?? '');
    this.load();
    void this.startLiveUpdates();
  }

  ngOnDestroy(): void {
    void this.liveUnsubscribe?.();
  }

  load(): void {
    this.loading.set(true);
    this.api.listRegulatoryDefinitions().subscribe({
      next: defs => {
        this.definitions.set(defs);
        this.api.listRegulatoryJobs({
          status: this.statusFilter(),
          definitionId: null
        }).subscribe({
          next: jobs => {
            this.jobs.set(jobs);
            this.loading.set(false);
            this.permissionDenied.set(false);
            this.error.set('');
            this.info.set('');
          },
          error: err => {
            this.jobs.set([]);
            this.loading.set(false);
            this.permissionDenied.set(err?.status === 403);
            this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
          }
        });
      },
      error: err => {
        this.definitions.set([]);
        this.jobs.set([]);
        this.loading.set(false);
        this.permissionDenied.set(err?.status === 403);
        this.error.set(err?.status === 403 ? '' : this.t('insurance.states.loadError'));
      }
    });
  }

  filteredRows(): InsuranceRegulatoryReportJobListItem[] {
    const searchValue = this.search().trim().toLowerCase();
    const jurisdiction = this.jurisdictionFilter();
    const status = this.statusFilter();
    const filtered = this.jobs().filter(item => {
      const matchSearch =
        !searchValue ||
        item.definitionCode.toLowerCase().includes(searchValue) ||
        item.definitionName.toLowerCase().includes(searchValue) ||
        item.jurisdiction.toLowerCase().includes(searchValue) ||
        item.status.toLowerCase().includes(searchValue);
      const matchJurisdiction = !jurisdiction || item.jurisdiction === jurisdiction;
      const matchStatus = !status || item.status === status;
      return matchSearch && matchJurisdiction && matchStatus;
    });

    const field = this.sortField();
    const order = this.sortOrder();
    return [...filtered].sort((left, right) => compareSortValues(left[field], right[field], order));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { q: this.search().trim() || null },
      queryParamsHandling: 'merge'
    });
  }

  onStatusFilterChange(value: string | null): void {
    this.statusFilter.set(value);
    this.load();
  }

  onJurisdictionFilterChange(value: string | null): void {
    this.jurisdictionFilter.set(value);
  }

  onSelectionChange(selection: InsuranceRegulatoryReportJobListItem[] | InsuranceRegulatoryReportJobListItem | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(jobId: string): void {
    void this.router.navigate(['/insurance/regulatory/jobs', jobId], {
      queryParams: { q: this.search().trim() || null }
    });
  }

  applyBulk(): void {
    if (this.selectedRows().length === 0) {
      this.info.set(this.t('insurance.actions.bulkNothingSelected'));
      return;
    }

    this.info.set(this.t('insurance.actions.bulkQueued'));
  }

  exportRows(): void {
    const rows = this.filteredRows();
    if (rows.length === 0) {
      this.info.set(this.t('insurance.actions.exportEmpty'));
      return;
    }

    downloadCsv(
      'insurance-regulatory-jobs.csv',
      ['DefinitionCode', 'DefinitionName', 'Jurisdiction', 'PeriodFrom', 'PeriodTo', 'Status', 'SubmittedAtUtc'],
      rows.map(item => [
        item.definitionCode,
        item.definitionName,
        item.jurisdiction,
        item.periodFrom,
        item.periodTo,
        item.status,
        item.submittedAtUtc ?? ''
      ]));
    this.info.set(this.t('insurance.actions.exportReady'));
  }

  sortBy(field: RegulatoryJobSortField): void {
    const next = toggleSortField(field, this.sortField(), this.sortOrder());
    this.sortField.set(next.field);
    this.sortOrder.set(next.order);
  }

  createDefinition(): void {
    const now = Date.now().toString().slice(-6);
    const payload: CreateInsuranceRegulatoryDefinitionPayload = {
      legalEntityId: crypto.randomUUID(),
      code: `IR-REG-${now}`,
      name: `Iran Regulatory ${now}`,
      jurisdiction: 'IR',
      schemaVersion: '1.0',
      templateJson: '{}'
    };
    this.api.createRegulatoryDefinition(payload).subscribe({
      next: () => this.load(),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  createJob(): void {
    const definition = this.definitions()[0];
    if (!definition) {
      this.error.set(this.t('insurance.states.loadError'));
      return;
    }
    const now = new Date();
    const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    const to = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 0, 23, 59, 59));
    const payload: CreateInsuranceRegulatoryReportJobPayload = {
      definitionId: definition.id,
      periodFrom: from.toISOString(),
      periodTo: to.toISOString()
    };
    this.api.createRegulatoryJob(payload).subscribe({
      next: () => this.load(),
      error: () => this.error.set(this.t('insurance.states.loadError'))
    });
  }

  statusKey(status: string): string {
    return `insurance.regulatory.console.status.${status.charAt(0).toLowerCase()}${status.slice(1)}`;
  }

  private async startLiveUpdates(): Promise<void> {
    const tenantId = this.currentContext.context()?.tenantId;
    if (!tenantId) {
      return;
    }

    const live = await subscribeInsuranceOperationsLiveUpdates(tenantId, this.liveUpdateService, () => this.load());
    this.liveUnsubscribe = live.unsubscribe;
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
